import React from 'react';
import styled, { css } from 'styled-components';
import { crTheme } from '@comicrelief/component-library';
import {
  CheckboxIcon,
  CheckboxIconWrapper,
  CheckboxVisual,
  CheckboxContentWrapper,
  CheckboxTextWrapper,
  HiddenCheckbox
} from './CheckboxWrapper';

const CheckboxWithSvgWrapper = styled.label<{ checked?: boolean }>`
  display: block;
  margin: 0 0 2rem;
  padding: 1.25rem;
  border-radius: 0.5rem;
  cursor: pointer;

  background-color: ${crTheme.color('white')};
  color: ${crTheme.color('black')};
  box-shadow: 0 0 0 1px ${crTheme.color('grey')};

  &:focus-within {
    box-shadow: 0 0 0 2px ${crTheme.color('blue_donate_interact')};
  }

  ${({ checked }) => checked && css`
    background-color: ${crTheme.color('blue_donate')};
    color: ${crTheme.color('white')};
    &:hover {
      background-color: ${crTheme.color('blue_donate_interact')};
    }
  `}
`;

/**
 * Generic checkbox component with SVG icon support. The icon always appears on the left.
 * TODO: Move this to the component library when we know it's working and we don't need any more props or whatever.
 *
 * @param checked - Whether the checkbox is checked
 * @param htmlFor - The htmlFor attribute for the label
 * @param dataTest - The data-test attribute for testing
 * @param svgFilePath - Path to the SVG icon to display
 * @param onChange - Callback function when checkbox state changes
 * @param hiddenCheckboxId - ID for the hidden checkbox input
 * @param children - React children to render within the CheckboxTextWrapper
 */
interface CheckboxWithSvgProps {
  checked: boolean;
  htmlFor: string;
  dataTest?: string;
  svgFilePath: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  hiddenCheckboxId: string;
  children: React.ReactNode;
}

export default function CheckboxWithSvg({
  checked,
  htmlFor,
  dataTest = '',
  svgFilePath,
  onChange,
  hiddenCheckboxId,
  children
}: CheckboxWithSvgProps) {
  return (
    <CheckboxWithSvgWrapper checked={checked} htmlFor={htmlFor}>
      <CheckboxContentWrapper data-test={dataTest}>
        <CheckboxIconWrapper>
          <CheckboxIcon
            src={svgFilePath}
            alt="Checkbox icon"
            checked={checked}
          />
        </CheckboxIconWrapper>

        <CheckboxTextWrapper checked={checked}>
          {children}
        </CheckboxTextWrapper>

        <CheckboxVisual checked={checked} $checkboxPlacement="right" />
      </CheckboxContentWrapper>

      <HiddenCheckbox
        id={hiddenCheckboxId}
        checked={checked}
        onChange={onChange}
      />
    </CheckboxWithSvgWrapper>
  );
}
